import React, { useEffect, useState } from "react";
import { formatSecondGetTime } from "../../utils/formatSecondGetTime";
import { getMP3, playAudio } from "../../utils/indexedDB"; // Import IndexedDB function
import { useCountdown } from "../../hooks/useCountdown";
import { useExam } from "../../context/ExamContext";
import { FaHeadphones, FaClock, FaInfoCircle, FaPlay, FaCheckCircle } from "react-icons/fa";
import { color } from "framer-motion";
import { buildAudioKey, DEMO_AUDIO_KEY } from "../../constants/storageKeys";

const ListeningIntro = ({ onStart, timeLeft, isDemoExam }) => {
    const { uprId } = useExam();

    useEffect(() => {
        // Prevent scrolling
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "auto"; // Reset on unmount
        };
    }, []);
    const countingTimeLeft = useCountdown();

    const handlePlayMp3 = async (filename) => {
        try {
            const audioBlob = await getMP3(filename);
            if (!audioBlob) {
                console.error("No MP3 found in IndexedDB for", filename);
                return;
            }

            playAudio(audioBlob, filename, countingTimeLeft);
        } catch (error) {
            console.error("Error playing MP3:", error);
        }
    }

    return (
        <div className="flex justify-center items-center min-h-screen sectionIntros" style={{ backgroundColor: 'var(--background-color)', zIndex: 9999 }}>
            <div className="w-4/5 max-w-4xl bg-var(--background-color) border border-var(--border-color) shadow-2xl rounded-2xl p-12 text-center relative overflow-hidden">
                {/* Decorative background elements */}
                <div className="absolute top-4 right-4 w-16 h-16 bg-[#FC532B] rounded-full opacity-20"></div>
                <div className="absolute bottom-4 left-4 w-12 h-12 bg-[#FC532B] rounded-full opacity-15"></div>

                {/* Header Section */}
                <div className="mb-10">
                    <div className="flex items-center justify-center mb-6">
                        <div className="w-20 h-20 bg-[#FC532B] rounded-full flex items-center justify-center mr-6 shadow-lg">
                            <FaHeadphones style={{ color: "red" }} className="text-white text-3xl faHeadphones" />
                        </div>
                        <div className="text-left">
                            <h2 className="text-4xl font-bold text-var(--text-color) mb-2">IELTS Academic Listening</h2>
                            <div className="flex items-center text-var(--text-color) opacity-70">
                                <FaClock className="mr-3 text-[#FC532B]" />
                                <span className="font-medium text-lg">Duration: 30 minutes</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Single Card Content */}
                <div className="bg-var(--background-color) border border-var(--border-color) p-8 rounded-xl border-l-4 mb-8">
                    <div className="grid md:grid-cols-2 gap-8">
                        {/* Instructions Section */}
                        <div className="text-left">
                            <h3 className="font-bold text-xl text-[#FC532B] mb-6 flex items-center">
                                <FaInfoCircle className="mr-3 text-[#FC532B]" />
                                INSTRUCTIONS TO CANDIDATES
                            </h3>
                            <div className="space-y-4">
                                <div className="flex items-start group">
                                    <FaCheckCircle className="text-[#FC532B] mt-1 mr-4 flex-shrink-0 group-hover:scale-110 transition-transform" />
                                    <span className="text-var(--text-color) leading-relaxed">Listen to the instructions for each part of the test carefully.</span>
                                </div>
                                <div className="flex items-start group">
                                    <FaCheckCircle className="text-[#FC532B] mt-1 mr-4 flex-shrink-0 group-hover:scale-110 transition-transform" />
                                    <span className="text-var(--text-color) leading-relaxed">Answer <strong className="text-[#FC532B]">all</strong> the questions.</span>
                                </div>
                                <div className="flex items-start group">
                                    <FaCheckCircle className="text-[#FC532B] mt-1 mr-4 flex-shrink-0 group-hover:scale-110 transition-transform" />
                                    <span className="text-var(--text-color) leading-relaxed">You can make notes while you listen.</span>
                                </div>
                                <div className="flex items-start group">
                                    <FaCheckCircle className="text-[#FC532B] mt-1 mr-4 flex-shrink-0 group-hover:scale-110 transition-transform" />
                                    <span className="text-var(--text-color) leading-relaxed">You will have time to check your answers after each section.</span>
                                </div>
                                <div className="flex items-start group">
                                    <FaCheckCircle className="text-[#FC532B] mt-1 mr-4 flex-shrink-0 group-hover:scale-110 transition-transform" />
                                    <span className="text-var(--text-color) leading-relaxed">After the final recording, you will have <strong className="text-[#FC532B]">2 minutes</strong> to review all your answers before submitting.</span>
                                </div>
                            </div>
                        </div>

                        {/* Information Section */}
                        <div className="text-left">
                            <h3 className="font-bold text-xl text-[#FC532B] mb-6 flex items-center">
                                <FaInfoCircle className="mr-3 text-[#FC532B]" />
                                INFORMATION FOR CANDIDATES
                            </h3>
                            <div className="space-y-4">
                                <div className="flex items-start group">
                                    <FaCheckCircle className="text-[#FC532B] mt-1 mr-4 flex-shrink-0 group-hover:scale-110 transition-transform" />
                                    <span className="text-var(--text-color) leading-relaxed">There are <strong className="text-[#FC532B]">4 sections</strong> and a total of <strong className="text-[#FC532B]">40 questions</strong>.</span>
                                </div>
                                <div className="flex items-start group">
                                    <FaCheckCircle className="text-[#FC532B] mt-1 mr-4 flex-shrink-0 group-hover:scale-110 transition-transform" />
                                    <span className="text-var(--text-color) leading-relaxed">Each correct answer receives <strong className="text-[#FC532B]">1 mark</strong>.</span>
                                </div>
                                <div className="flex items-start group">
                                    <FaCheckCircle className="text-[#FC532B] mt-1 mr-4 flex-shrink-0 group-hover:scale-110 transition-transform" />
                                    <span className="text-var(--text-color) leading-relaxed">The test automatically moves to the next question or section.</span>
                                </div>
                                <div className="flex items-start group">
                                    <FaCheckCircle className="text-[#FC532B] mt-1 mr-4 flex-shrink-0 group-hover:scale-110 transition-transform" />
                                    <span className="text-var(--text-color) leading-relaxed">You can navigate between questions within the same section.</span>
                                </div>
                                <div className="flex items-start group">
                                    <FaCheckCircle className="text-[#FC532B] mt-1 mr-4 flex-shrink-0 group-hover:scale-110 transition-transform" />
                                    <span className="text-var(--text-color) leading-relaxed">Use the on-screen volume control to adjust audio if needed.</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action Section */}
                <div className="flex flex-col items-center">
                    {timeLeft !== 0 && timeLeft != null && timeLeft !== undefined ? (
                        <div className="bg-[#FC532B] text-white px-10 py-5 rounded-full shadow-lg">
                            <p className="font-semibold text-xl">Exam will start in {formatSecondGetTime(countingTimeLeft)} seconds...</p>
                        </div>
                    ) : (
                        <button
                            className="bg-[#FC532B] hover:bg-[#e04a26] text-white font-bold px-10 py-5 rounded-full shadow-lg transition-all duration-300 transform hover:scale-105 flex items-center text-lg"
                            onClick={() => {
                                onStart();
                                if (timeLeft === 0) {
                                    handlePlayMp3((!isDemoExam ? buildAudioKey(uprId) : DEMO_AUDIO_KEY));
                                }
                            }}
                        >
                            <FaPlay className="mr-3" />
                            Continue to Exam
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ListeningIntro;